/**
 * StageFormModals — the two small forms on Admin > Stage Pipeline, as FTP's
 * popups (admin-stage-add.php, admin-stage-pipeline-details.php), in the
 * Prospera admin modal chrome:
 *
 *   AddStageModal       name (required, 25 max) and stage type (Custom or a
 *                       free global type); the stage goes last in the pipeline.
 *   PipelineTitleModal  add or rename a pipeline: a title of 1–25 characters,
 *                       not "Default", not another pipeline's (any case).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { DialogField, DialogFooterLayout, DialogHeading, fieldClasses, type FieldState } from '@/components/admin/DialogChrome';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/services/apiClient';
import {
  CUSTOM_STAGE_TYPE,
  PIPELINE_TITLE_MAX,
  type Pipeline,
  type StageTypeOption,
} from '@/services/stagePipelineAdmin.service';
import { stageNameError } from './StageEditRow';

// ─── Add stage ────────────────────────────────────────────────────────────────

export function AddStageModal({
  stageTypes,
  onSave,
  onClose,
}: {
  stageTypes: StageTypeOption[];
  onSave: (name: string, typeId: number) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState<number>(CUSTOM_STAGE_TYPE);
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const nameError = stageNameError(name);
  const shownError = touched ? nameError : null;
  const state: FieldState = shownError || serverError ? 'error' : 'none';

  async function handleSave() {
    setTouched(true);
    if (nameError || saving) return;
    setSaving(true);
    try {
      await onSave(name.trim(), typeId);
    } catch (err) {
      setServerError(getErrorMessage(err, 'The stage could not be added.'));
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={<DialogHeading title="Add Stage" description="It goes last in the pipeline, with the starting rules in every pipeline." />}
      footer={
        <DialogFooterLayout errors={nameError ? [nameError] : []} isCreating>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={Boolean(nameError)}>
            Add
          </Button>
        </DialogFooterLayout>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <DialogField label="Stage Name" htmlFor="add-stage-name" required state={state} labelHint={shownError ? `— ${shownError.toLowerCase()}` : undefined}>
          <Input
            id="add-stage-name"
            autoFocus
            placeholder="New Stage"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setTouched(true);
              setServerError(undefined);
            }}
            error={serverError}
            className={fieldClasses.input(state)}
          />
        </DialogField>
        <DialogField label="Stage Type" htmlFor="add-stage-type">
          <Select<number>
            id="add-stage-type"
            options={[{ value: CUSTOM_STAGE_TYPE, label: 'Custom' }, ...stageTypes.map((t) => ({ value: t.id, label: t.name }))]}
            value={typeId}
            onChange={(v) => setTypeId(v ?? CUSTOM_STAGE_TYPE)}
          />
        </DialogField>
      </form>
    </Modal>
  );
}

// ─── Pipeline title ───────────────────────────────────────────────────────────

/**
 * Why a pipeline title can't be saved, or null (KTD9). No 4-byte characters
 * (emoji): the column is utf8mb3, and the API refuses them.
 */
export function pipelineTitleError(title: string, pipelines: Pipeline[], selfId: number | null): string | null {
  const trimmed = title.trim();
  if (trimmed === '') return 'A title is required';
  if (trimmed.length > PIPELINE_TITLE_MAX) return `At most ${PIPELINE_TITLE_MAX} characters`;
  if (/[\u{10000}-\u{10FFFF}]/u.test(trimmed)) return 'Emoji are not allowed';
  if (trimmed.toLowerCase() === 'default') return 'Default is reserved';
  if (pipelines.some((p) => p.id !== selfId && p.name.trim().toLowerCase() === trimmed.toLowerCase())) return 'Title already used by another pipeline';
  return null;
}

export function PipelineTitleModal({
  pipeline,
  pipelines,
  onSave,
  onClose,
}: {
  /** The pipeline to rename; null to add one. */
  pipeline: Pipeline | null;
  pipelines: Pipeline[];
  onSave: (title: string) => Promise<void>;
  onClose: () => void;
}) {
  const isNew = pipeline === null;
  const [title, setTitle] = useState(pipeline?.name ?? '');
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const error = pipelineTitleError(title, pipelines, pipeline?.id ?? null);
  const shownError = touched ? error : null;
  const state: FieldState = shownError || serverError ? 'error' : 'none';

  async function handleSave() {
    setTouched(true);
    if (error || saving) return;
    setSaving(true);
    try {
      await onSave(title.trim());
    } catch (err) {
      setServerError(getErrorMessage(err, 'The pipeline could not be saved.'));
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={
        <DialogHeading
          title={isNew ? 'Add Pipeline' : 'Rename Pipeline'}
          variant={isNew ? 'default' : 'edit'}
          description={isNew ? 'It starts with a copy of the Default rules.' : 'Plan types on it keep using it.'}
        />
      }
      footer={
        <DialogFooterLayout errors={error ? [error] : []} isCreating={isNew}>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={Boolean(error)}>
            {isNew ? 'Add' : 'Update'}
          </Button>
        </DialogFooterLayout>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <DialogField label="Pipeline Title" htmlFor="pipeline-title" required state={state} labelHint={shownError ? `— ${shownError}` : undefined}>
          <Input
            id="pipeline-title"
            autoFocus
            placeholder="New Pipeline"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTouched(true);
              setServerError(undefined);
            }}
            error={serverError}
            className={fieldClasses.input(state)}
          />
        </DialogField>
      </form>
    </Modal>
  );
}
